/* eslint-disable jsx-a11y/control-has-associated-label, react/no-danger, react/jsx-props-no-spreading */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useCallback, useEffect } from 'react';

import useKeyboardKeys, { KEYS } from '../../hooks/useKeyboardKeys';
import useSwipe from '../../hooks/useSwipe';
import useWebp from '../../hooks/useWebp';
import * as AppPropTypes from '../../lib/PropTypes';
import { getImageAtSize } from '../../lib/utils';

import PillButton from '../buttons/Pill';
import Arrow from '../icons/Arrow';

import styles from '../../../styles/partials/gallery-viewer.module.scss';

const propTypes = {
    images: AppPropTypes.images.isRequired,
    image: AppPropTypes.image,
    onClickClose: PropTypes.func.isRequired,
    className: PropTypes.string,
};

const defaultProps = {
    image: null,
    className: null,
};

const Viewer = ({ images, image: providedImage, onClickClose, className }) => {
    const [currentImage, setCurrentImage] = useState(providedImage);
    const currentIndex = currentImage !== null ? images.findIndex((it) => it === currentImage) : 0;

    const webp = useWebp();

    const nextSlide = useCallback(() => {
        const nextIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
        setCurrentImage(images[nextIndex]);
    }, [images, currentIndex, setCurrentImage]);

    const previousSlide = useCallback(() => {
        const nextIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
        setCurrentImage(images[nextIndex]);
    }, [images, currentIndex, setCurrentImage]);

    useEffect(() => {
        if (providedImage !== currentImage) {
            setCurrentImage(providedImage);
        }
    }, [providedImage]);

    useKeyboardKeys({
        [KEYS.ARROW_LEFT]: previousSlide,
        [KEYS.ARROW_RIGHT]: nextSlide,
        [KEYS.ESCAPE]: onClickClose,
    });

    const bindSwipe = useSwipe({
        onSwipeRight: previousSlide,
        onSwipeLeft: nextSlide,
    });

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
        >
            <div className={styles.slideshow} {...bindSwipe()}>
                <PillButton
                    className={classNames([styles.arrow, styles.arrowLeft])}
                    icon={<Arrow direction="left" />}
                    onClick={previousSlide}
                    isDark
                />

                <div className={styles.frame}>
                    <div className={styles.slides}>
                        {images.map((image, index) => {
                            const imageAtSize = getImageAtSize(image, 'large', webp);
                            const valid =
                                index === currentIndex ||
                                index === currentIndex + 1 ||
                                index === currentIndex - 1;
                            return (
                                <div
                                    key={`slide-${image.id}`}
                                    className={classNames([
                                        styles.slide,
                                        {
                                            [styles.previous]: index < currentIndex,
                                            [styles.current]: index === currentIndex,
                                            [styles.next]: index > currentIndex,
                                        },
                                    ])}
                                >
                                    <div
                                        className={styles.image}
                                        style={{
                                            backgroundImage: valid
                                                ? `url("${imageAtSize.url}") `
                                                : null,
                                        }}
                                    />
                                    {image.title !== null || image.description !== null ? (
                                        <div className={styles.caption}>
                                            {image.title !== null ? (
                                                <div
                                                    className={styles.title}
                                                    dangerouslySetInnerHTML={{
                                                        __html: image.title,
                                                    }}
                                                />
                                            ) : null}
                                            {image.description !== null ? (
                                                <div
                                                    className={styles.description}
                                                    dangerouslySetInnerHTML={{
                                                        __html: image.description,
                                                    }}
                                                />
                                            ) : null}
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <PillButton
                    className={classNames([styles.arrow, styles.arrowRight])}
                    icon={<Arrow direction="right" />}
                    onClick={nextSlide}
                    isDark
                />
            </div>
        </div>
    );
};

Viewer.propTypes = propTypes;
Viewer.defaultProps = defaultProps;

export default Viewer;
